/**
 * Campaign console – one campaign at `/campaigns/:slug`.
 *
 * The owner decides, watches and steps in; the seat runs the campaign.
 * Header (seat, mailbox, delivery), goal bar, opportunity strip, the funnel
 * over the fixed member states, "Next up", then the tabs (`?tab=`):
 * Bundles · Threads · People · Results · Rules (+ Markets for B2G) · Do not
 * contact. A `waves` campaign (B2G) takes its bundles, send plan, markets
 * and suppressions from the outreach waves (`useWavesData`); a `drafts`
 * campaign shows the bundles its seat filed – email items and tasks
 * (`BundlesTab`). A `waves` campaign lists its bundles (emails and tasks
 * outside the waves) under the waves.
 *
 * URL state: `?tab=`, `?bundle=<id>`, `?wave=<id>` + `?filter=`
 * (waves), `?thread=<id>` (threads). An old `/campaigns/<uuid>` link
 * resolves to the slug.
 */
import { Navigate, Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";

import { campaignsApi } from "../api/campaigns";
import { ApiError } from "../api/client";
import { useConsoleCampaign, type CampaignDetail } from "../api/console";
import { MarketsTable } from "../components/b2g/MarketsTable";
import { SendPlanPanel } from "../components/b2g/SendPlanPanel";
import { SenderBar } from "../components/b2g/SenderBar";
import { SuppressionsTable } from "../components/b2g/SuppressionsTable";
import { DoNotContactTable, FunnelStrip, GoalBar, OpportunityStrip, RulesList } from "../components/console/parts";
import { PeopleTable } from "../components/console/PeopleTable";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { ErrorStrip, QueryBoundary } from "../components/QueryBoundary";
import { berlinDateTime, deliveryLabel, isUuid, replyRate } from "../lib/console";
import { useSenderControls } from "../lib/hooks/useSenderControls";
import { BundleDetail, BundleList } from "./campaign/BundlesTab";
import { ThreadsTab } from "./campaign/ThreadsTab";
import { useWavesData, type WavesData } from "./campaign/useWavesData";
import { WavesBundles, WavesStrategy } from "./campaign/WavesBundles";

type Tab = "bundles" | "threads" | "people" | "results" | "rules" | "markets" | "dnc";
const TABS: Tab[] = ["bundles", "threads", "people", "results", "rules", "markets", "dnc"];

export function CampaignPage() {
  const { slug } = useParams<{ slug: string }>();
  if (isUuid(slug)) return <LegacyCampaignRedirect id={slug as string} />;
  return <ConsoleCampaign slug={slug as string} />;
}

/** An old `/campaigns/<uuid>` link (the step board) → the campaign's slug. */
function LegacyCampaignRedirect({ id }: { id: string }) {
  const { search } = useParamsSearch();
  const q = useQuery({ queryKey: ["campaigns", "by-id", id], queryFn: () => campaignsApi.get(id), retry: false });
  if (q.data?.data?.slug) return <Navigate to={`/campaigns/${q.data.data.slug}${search}`} replace />;
  return (
    <div className="page">
      {q.isLoading ? (
        <div className="py-10 text-center text-ink-low text-sm">Loading…</div>
      ) : (
        <div className="panel text-sm">
          No campaign with this id. <Link to="/campaigns" className="link">All campaigns</Link>
        </div>
      )}
    </div>
  );
}

function useParamsSearch() {
  const [sp] = useSearchParams();
  const s = sp.toString();
  return { search: s ? `?${s}` : "" };
}

function ConsoleCampaign({ slug }: { slug: string }) {
  const [sp, setSp] = useSearchParams();
  const query = useConsoleCampaign(slug);
  const engine = query.data?.campaign.engine;
  const rawTab = sp.get("tab") as Tab | null;
  const tab: Tab = rawTab && TABS.includes(rawTab) && (rawTab !== "markets" || engine === "waves") ? rawTab : "bundles";
  const waveId = sp.get("wave");
  const waves = useWavesData({ enabled: engine === "waves", waveId, tab });

  const setParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSp(next, { replace: true });
  };

  if (query.isError && query.data === undefined && query.error instanceof ApiError && query.error.status === 404) {
    return (
      <div className="page">
        <PageHeader title="Campaign not on the console" />
        <div className="panel text-sm">
          <span className="font-mono">{slug}</span> has no console config. A campaign joins the console with a{" "}
          <span className="font-mono">console</span> object via campaign_upsert.{" "}
          <Link to="/campaigns" className="link">
            All campaigns
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <QueryBoundary query={query}>
        {(detail) => <CampaignBody detail={detail} tab={tab} setParams={setParams} sp={sp} waves={waves} />}
      </QueryBoundary>
    </div>
  );
}

function CampaignBody({
  detail,
  tab,
  setParams,
  sp,
  waves,
}: {
  detail: CampaignDetail;
  tab: Tab;
  setParams: (patch: Record<string, string | null>) => void;
  sp: URLSearchParams;
  waves: WavesData;
}) {
  const c = detail.campaign;
  const isWaves = c.engine === "waves";
  const seat = c.seat_name ?? c.seat_slug;
  const bundleId = sp.get("bundle");
  const waveId = sp.get("wave");
  const threadId = sp.get("thread");
  const filter = ["open", "active", "all"].includes(sp.get("filter") ?? "") ? (sp.get("filter") as string) : "open";
  const openThreads = detail.threads.filter((t) => t.status === "open").length;
  const openBundles = isWaves
    ? (waves.waves.data?.waves ?? []).filter((w) => w.status === "pending_approval").length
    : detail.bundles.filter((b) => b.state === "open").length;

  const tabs: { value: Tab; label: string }[] = [
    { value: "bundles", label: isWaves ? `Waves · ${openBundles} for you` : `Bundles · ${openBundles} open` },
    { value: "threads", label: `Threads · ${openThreads}` },
    { value: "people", label: `People · ${detail.people.length}` },
    { value: "results", label: "Results" },
    { value: "rules", label: "Rules" },
  ];
  if (isWaves) tabs.push({ value: "markets", label: "Markets" });
  tabs.push({ value: "dnc", label: "Do not contact" });

  const tabHref = (value: Tab) => `?tab=${value}`;
  const openThread = (id: string) => setParams({ tab: "threads", thread: id, bundle: null, wave: null });

  return (
    <>
      <PageHeader
        title={c.name}
        description={c.goal ?? undefined}
        actions={
          <Link to="/campaigns" className="btn btn-sm btn-ghost">
            ← All campaigns
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-low mb-4">
        <span>
          Seat <span className="text-ink font-semibold">{seat}</span>
        </span>
        {c.mailbox ? <span className="font-mono">{c.mailbox}</span> : null}
        <Pill value={deliveryLabel(c.delivery)} tone={c.delivery === "server" ? "ok" : "muted"} />
        <Pill value={c.status} />
      </div>

      {isWaves ? <CampaignSenderBar /> : null}
      {c.goal_bar ? <GoalBar bar={c.goal_bar} /> : null}
      {c.opportunity_stages.length ? <OpportunityStrip stages={c.opportunity_stages} /> : null}

      <FunnelStrip funnel={c.funnel} closed={c.closed} parked={c.parked} unit={c.unit} yourTurn={c.your_turn} />

      {isWaves ? (
        waves.plan.data ? (
          <SendPlanPanel plan={waves.plan.data} onOpenWave={(id) => setParams({ tab: "bundles", wave: id })} />
        ) : waves.plan.isError ? (
          <div className="mb-6">
            <ErrorStrip
              message="Couldn't load the send plan."
              onRetry={() => void waves.plan.refetch()}
              retrying={waves.plan.isFetching}
            />
          </div>
        ) : null
      ) : detail.awaiting_send.length ? (
        <AwaitingSend detail={detail} />
      ) : null}

      <nav className="subnav" aria-label="Campaign views">
        {tabs.map((t) => (
          <Link key={t.value} to={tabHref(t.value)} replace className={clsx(tab === t.value && !bundleId && !waveId && "is-active")}>
            {t.label}
          </Link>
        ))}
      </nav>

      {tab === "bundles" && bundleId ? (
        <BundleDetail bundleId={bundleId} onBack={() => setParams({ bundle: null })} onOpenThread={openThread} />
      ) : null}
      {tab === "bundles" && isWaves && !bundleId ? (
        <>
          <WavesBundles data={waves} seat={seat} waveId={waveId} filter={filter} setParams={setParams} />
          {detail.bundles.length && !waveId ? (
            <div className="mt-6">
              <h2 className="font-heading font-semibold text-ink mb-2">Bundles – emails and tasks outside the waves</h2>
              <BundleList bundles={detail.bundles} seat={seat} onOpen={(id) => setParams({ bundle: id })} />
            </div>
          ) : null}
        </>
      ) : null}
      {tab === "bundles" && !isWaves && !bundleId ? (
        <BundleList bundles={detail.bundles} seat={seat} onOpen={(id) => setParams({ bundle: id })} />
      ) : null}
      {tab === "threads" ? (
        <ThreadsTab threads={detail.threads} focusId={threadId} onFocus={(id) => setParams({ thread: id })} />
      ) : null}
      {tab === "people" ? <PeopleTable people={detail.people} onOpenThread={openThread} /> : null}
      {tab === "results" ? (
        <>
          <ResultsPanel detail={detail} />
          {isWaves ? <WavesStrategy data={waves} /> : null}
        </>
      ) : null}
      {tab === "rules" ? <RulesList rules={c.rules} /> : null}
      {tab === "markets" && isWaves ? (
        <QueryBoundary
          query={waves.markets}
          isEmpty={(d) => d.markets.length === 0}
          empty={
            <div className="empty-state">
              <div className="title">No markets yet</div>
              <p className="hint">{seat} builds the long list with market_upsert.</p>
            </div>
          }
        >
          {(d) => (
            <MarketsTable
              markets={d.markets}
              germany={d.germany}
              busyIso={waves.busy}
              onDecide={async (m, dec) => {
                await waves.decide.mutateAsync({ m, d: dec }).catch(() => undefined);
              }}
            />
          )}
        </QueryBoundary>
      ) : null}
      {tab === "dnc" ? (
        <div className="space-y-4">
          {isWaves ? (
            <QueryBoundary query={waves.suppressions}>
              {(rows) => (
                <SuppressionsTable rows={rows} lifting={waves.lift.isPending} onLift={(id) => waves.lift.mutate(id)} />
              )}
            </QueryBoundary>
          ) : null}
          <DoNotContactTable rows={c.do_not_contact} />
        </div>
      ) : null}
    </>
  );
}

function CampaignSenderBar() {
  const senders = useSenderControls();
  return <SenderBar {...senders} />;
}

function AwaitingSend({ detail }: { detail: CampaignDetail }) {
  const mailbox = detail.campaign.mailbox ?? "your mailbox";
  return (
    <div className="panel panel--flush mb-4">
      <div className="panel-header">
        <h2>Next up</h2>
        <span className="text-xs text-ink-low">approved – the draft lands in {mailbox}, you send it</span>
      </div>
      <table className="table">
        <tbody>
          {detail.awaiting_send.map((a) => (
            <tr key={a.review_id}>
              <td className="font-mono text-xs text-ink-low whitespace-nowrap">{berlinDateTime(a.reviewed_at)}</td>
              <td className="font-mono text-sm">{a.to.join(", ")}</td>
              <td className="text-sm">{a.title}</td>
              <td className="text-right">
                <Link to={`?tab=bundles&bundle=${a.bundle_id}`} className="link text-sm">
                  bundle →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultsPanel({ detail }: { detail: CampaignDetail }) {
  const r = detail.results;
  const c = detail.campaign;
  return (
    <div className="panel">
      <div className="text-xs text-ink-low mb-3">
        Real replies only – automatic replies and bounces do not count. Counted over every thread of the campaign.
      </div>
      <dl className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-ink-low">Threads contacted</dt>
        <dd className="font-mono tnum text-ink">{r.contacted}</dd>
        <dt className="text-ink-low">Drew a real reply</dt>
        <dd className="font-mono tnum text-ink">{r.replied}</dd>
        <dt className="text-ink-low">Reply rate</dt>
        <dd className="font-mono tnum text-ink">{replyRate(r.replied, r.contacted)}</dd>
        <dt className="text-ink-low">Closed</dt>
        <dd className="text-ink">
          {Object.entries(c.closed)
            .filter(([, n]) => (n ?? 0) > 0)
            .map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`)
            .join(" · ") || "–"}
        </dd>
        <dt className="text-ink-low">Parked</dt>
        <dd className="font-mono tnum text-ink">{c.parked}</dd>
      </dl>
    </div>
  );
}
